import React, { useMemo } from 'react';
import { Text, Box, Skeleton } from "@mantine/core";
import { Card } from "@xplatform/design-system";
import { BarChart } from '@mantine/charts';
import type { CostTimeSeriesViewModel } from '@models/cost';
import './EventCountChart.scss';

interface EventCountChartProps {
    data: CostTimeSeriesViewModel | null;
    isLoading: boolean;
}

const EventCountChart: React.FC<EventCountChartProps> = ({ data, isLoading }) => {
    const chartData = useMemo(() => {
        if (!data) return [];
        return data.dataPoints.map((point) => ({
            timestamp: new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                hour: data.granularity === 'hour' ? 'numeric' : undefined,
            }).format(point.timestamp),
            Events: point.eventCount,
        }));
    }, [data]);

    const totalEvents = useMemo(() => {
        if (!data) return 0;
        return data.dataPoints.reduce((sum, point) => sum + point.eventCount, 0);
    }, [data]);

    if (isLoading) {
        return (
            <Card>
                <Skeleton height={32} width="40%" mb="sm" />
                <Skeleton height={300} mt="sm" />
            </Card>
        );
    }

    if (!data || chartData.length === 0) {
        return (
            <Card>
                <Text fw={600} size="lg" mb={4}>Event Count Trend</Text>
                <Text>No data available. Select a date range to view event trends.</Text>
            </Card>
        );
    }

    return (
        <Card className="event-count-chart-card" withBorder>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <Box>
                    <Text fw={600} size="lg" mb={4}>Event Count Trend</Text>
                    <Text size="sm" c="dimmed">
                        {data.granularity.charAt(0).toUpperCase() + data.granularity.slice(1)} breakdown
                    </Text>
                </Box>
                <Box className="total-indicator">
                    <Text size="sm" c="dimmed">Total Events</Text>
                    <Text fw={600} size="lg" c="brand">{totalEvents.toLocaleString()}</Text>
                </Box>
            </Box>
            <BarChart
                h={300}
                data={chartData}
                dataKey="timestamp"
                series={[{ name: 'Events', color: 'brand.5' }]}
                valueFormatter={(v) => v.toLocaleString()}
                withLegend
                withTooltip={false}
            />
        </Card>
    );
};

export default EventCountChart;
